import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Banner,
  Toast,
  Frame,
  Badge,
  Box,
  Icon,
  Tooltip,
  Tabs,
  ProgressBar,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris'
import {
  SaveIcon, ExternalIcon, ClipboardIcon, CheckIcon,
  StoreIcon, LockIcon, CodeIcon, PaintBrushFlatIcon,
  CheckCircleIcon, AlertTriangleIcon, ConnectIcon,
} from '@shopify/polaris-icons'
import apiClient from '../services/api'

export default function Settings() {
  const [toastMessage, setToastMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [tab, setTab] = useState(0)

  const shopDomain = localStorage.getItem('shop_domain') || ''

  const { data: storeData, isLoading: storeLoading } = useQuery({
    queryKey: ['store-settings'],
    queryFn: () => apiClient.get('/api/shopify/validate', {
      headers: { 'X-Shop-Domain': shopDomain },
    }).then(r => r.data.store),
  })

  const handleSave = async () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      setToastMessage('Settings saved')
    }, 500)
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setToastMessage(`${label} copied!`)
      setCopiedKey(label)
      setTimeout(() => setCopiedKey(null), 1800)
    })
  }

  const openThemeCustomizer = () => {
    window.open(`https://${shopDomain}/admin/themes`, '_blank', 'noopener')
  }

  const openAppEmbeds = () => {
    window.open(`https://${shopDomain}/admin/themes/current/editor?context=apps`, '_blank', 'noopener')
  }

  const publicKey = storeData?.form_public_key || ''
  const planActive = storeData?.plan_status === 'active'
  const planTone = planActive ? 'success' : 'attention'

  // ── Setup checklist — driven ONLY by real data we have ──────────────────
  const checklist = [
    { label: 'Store connected', done: true },
    { label: 'Public key generated', done: !!publicKey },
    { label: 'Plan active', done: planActive },
  ]
  const doneCount = checklist.filter(c => c.done).length
  const progressPct = Math.round((doneCount / checklist.length) * 100)

  // ── Reusable: connected code-box field with copy feedback ───────────────
  const CredentialField = ({ label, value, id }: { label: string; value: string; id: string }) => (
    <BlockStack gap="150">
      <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">{label}</Text>
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Box
          minWidth="0"
          width="100%"
          background="bg-surface-secondary"
          borderRadius="200"
          borderWidth="025"
          borderColor="border"
          padding="300"
        >
          <Text as="span" variant="bodySm" fontWeight="regular" tone={value ? undefined : 'subdued'}>
            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
              {value || 'Not available'}
            </span>
          </Text>
        </Box>
        <Tooltip content={copiedKey === id ? 'Copied!' : `Copy ${label}`}>
          <Button
            icon={copiedKey === id ? CheckIcon : ClipboardIcon}
            disabled={!value}
            tone={copiedKey === id ? 'success' : undefined}
            onClick={() => copyToClipboard(value, id)}
          >
            {copiedKey === id ? 'Copied' : 'Copy'}
          </Button>
        </Tooltip>
      </InlineStack>
    </BlockStack>
  )

  const StepRow = ({ n, children, last }: { n: number; children: React.ReactNode; last?: boolean }) => (
    <div style={{ display: 'flex', gap: '12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            background: 'var(--p-color-bg-fill-brand)',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text as="span" variant="bodySm" fontWeight="bold" tone="text-inverse">{n}</Text>
        </div>
        {!last && (
          <div
            style={{
              width: '2px',
              flex: 1,
              minHeight: '20px',
              borderLeft: '1px solid var(--p-color-border)',
              marginTop: '4px'
            }}
          />
        )}
      </div>
      <div style={{ paddingBottom: '12px', flex: 1 }}>
        <Text as="p" variant="bodySm">{children}</Text>
      </div>
    </div>
  )


  const TABS = [
    { id: 'setup', content: 'Setup Guide' },
    { id: 'credentials', content: 'API & Credentials' },
    { id: 'store', content: 'Store Info' },
  ]

  return (
    <Frame>
      <Page
        title="Settings"
        subtitle="Manage your store connection, theme integration, and API credentials."
        primaryAction={{
          content: saving ? 'Saving…' : 'Save Settings',
          icon: SaveIcon,
          loading: saving,
          onAction: handleSave,
        }}
      >
        <Box paddingBlockEnd="600">
          <Layout>

            {/* ── Hero: store identity + live setup progress ────────────────── */}
            <Layout.Section>
              <Card>
                {storeLoading ? (
                  <BlockStack gap="300">
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={2} />
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                      <InlineStack gap="300" blockAlign="center">
                        <Box background="bg-surface-secondary" borderRadius="200" padding="200">
                          <Icon source={StoreIcon} tone="base" />
                        </Box>
                        <BlockStack gap="050">
                          <Text as="h2" variant="headingMd">{storeData?.store_url || shopDomain}</Text>
                          <Text as="p" tone="subdued" variant="bodySm">Connected Shopify store</Text>
                        </BlockStack>
                      </InlineStack>
                      <Badge tone={planTone as any} size="large">
                        {(storeData?.plan_status || 'inactive').toUpperCase()}
                      </Badge>
                    </InlineStack>

                    <Divider />

                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Setup progress
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {doneCount} of {checklist.length} complete
                        </Text>
                      </InlineStack>
                      <ProgressBar progress={progressPct} tone={progressPct === 100 ? 'success' : 'primary'} size="small" />
                      <InlineStack gap="400" wrap>
                        {checklist.map(item => (
                          <InlineStack key={item.label} gap="100" blockAlign="center">
                            <Icon
                              source={item.done ? CheckCircleIcon : AlertTriangleIcon}
                              tone={item.done ? 'success' : 'subdued'}
                            />
                            <Text as="span" variant="bodySm" tone={item.done ? undefined : 'subdued'}>
                              {item.label}
                            </Text>
                          </InlineStack>
                        ))}
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                )}
              </Card>
            </Layout.Section>

            {/* ── Tabbed content ──────────────────────────────────────────── */}
            <Layout.Section>
              <Card padding="0">
                <Tabs tabs={TABS} selected={tab} onSelect={setTab}>
                  <Box padding="400">

                    {/* TAB 1 — Setup Guide */}
                    {tab === 0 && (
                      <BlockStack gap="500">

                        <BlockStack gap="400">
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="200" blockAlign="center">
                              <Icon source={PaintBrushFlatIcon} tone="base" />
                              <Text as="h2" variant="headingMd">Theme Integration</Text>
                            </InlineStack>
                            <Badge tone="info">App Embed</Badge>
                          </InlineStack>

                          <Text as="p" variant="bodyMd" tone="subdued">
                            Embed any form directly on your storefront by adding the <strong>Mobimint Form</strong> block
                            inside the Shopify Theme Customizer.
                          </Text>

                          <Divider />

                          <BlockStack gap="200">
                            <StepRow n={1}>
                              Click <strong>Open Theme Customizer</strong> below and select your active theme.
                            </StepRow>
                            <StepRow n={2}>
                              Navigate to the page where you want the form, then click
                              <strong> Add section → Apps → Mobimint Form</strong>.
                            </StepRow>
                            <StepRow n={3}>
                              In the block settings panel, paste your <strong>Store Public Key</strong>
                              (in the API & Credentials tab).
                            </StepRow>
                            <StepRow n={4} last>
                              Enter the <strong>Form Slug</strong> — visible in the Forms list (e.g. <code>contact-us</code>).
                              Then click Save.
                            </StepRow>
                          </BlockStack>

                          <Banner tone="warning" icon={AlertTriangleIcon}>
                            <p>
                              If you do not see <strong>Mobimint Form</strong> in the section picker, run{' '}
                              <code>shopify app deploy</code> in your terminal to publish the theme extension,
                              then re-open the customizer.
                            </p>
                          </Banner>

                          <InlineStack align="end">
                            <Button icon={ExternalIcon} variant="primary" onClick={openThemeCustomizer}>
                              Open Theme Customizer
                            </Button>
                          </InlineStack>
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="400">
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="200" blockAlign="center">
                              <Icon source={ConnectIcon} tone="base" />
                              <Text as="h2" variant="headingMd">App Embed (required — turns forms on)</Text>
                            </InlineStack>
                            <Badge tone="critical">Required</Badge>
                          </InlineStack>

                          <Text as="p" variant="bodyMd" tone="subdued">
                            This is the master switch for every <strong>Mobimint Form</strong> block you add above —
                            Shopify ships new themes with it switched off, so a form block added to your theme will
                            look fine in the editor preview but show <strong>nothing on your live store</strong> until
                            you turn this on. It's a one-time, store-wide setting — you only need to do this once, not
                            per form.
                          </Text>

                          <Divider />

                          <BlockStack gap="200">
                            <StepRow n={1}>
                              Click <strong>Open App Embeds</strong> below — it opens the Theme Editor directly on the
                              App Embeds panel.
                            </StepRow>
                            <StepRow n={2}>
                              Find <strong>Mobimint Forms</strong> in the list and toggle it <strong>ON</strong>.
                            </StepRow>
                            <StepRow n={3} last>
                              Click <strong>Save</strong>.
                            </StepRow>
                          </BlockStack>

                          <InlineStack gap="300">
                            <Button icon={ExternalIcon} variant="primary" onClick={openAppEmbeds}>
                              Open App Embeds
                            </Button>
                            <Button icon={ExternalIcon} onClick={openThemeCustomizer}>
                              Open Theme Customizer
                            </Button>
                          </InlineStack>
                        </BlockStack>

                      </BlockStack>
                    )}

                    {/* TAB 2 — API & Credentials */}
                    {tab === 1 && (
                      <BlockStack gap="500">
                        <InlineStack gap="200" blockAlign="center">
                          <Icon source={LockIcon} tone="base" />
                          <Text as="h2" variant="headingMd">Store Public Key</Text>
                        </InlineStack>
                        <Text as="p" tone="subdued" variant="bodySm">
                          Same key for every form on your store. Paste it into any Mobimint block or embed setting.
                        </Text>
                        <CredentialField label="Public Key" value={publicKey} id="Store Public Key" />

                        {!publicKey && (
                          <Banner tone="critical" icon={AlertTriangleIcon}>
                            <p>No public key generated yet. Contact support to enable form embedding.</p>
                          </Banner>
                        )}

                        <Divider />

                        <InlineStack gap="200" blockAlign="center">
                          <Icon source={CodeIcon} tone="base" />
                          <Text as="h2" variant="headingMd">API Information</Text>
                        </InlineStack>
                        <CredentialField label="API Base URL" value="https://api-forms.mobimint.co" id="API URL" />
                        <Text as="p" tone="subdued" variant="bodySm">
                          Use this URL and your Public Key when embedding forms via custom code or third-party
                          integrations.
                        </Text>
                      </BlockStack>
                    )}

                    {/* TAB 3 — Store Info */}
                    {tab === 2 && (
                      <BlockStack gap="400">
                        <Text as="h2" variant="headingMd">Store Information</Text>
                        {storeLoading ? (
                          <SkeletonBodyText lines={3} />
                        ) : (
                          <FormLayout>
                            <TextField
                              label="Shop Domain"
                              value={storeData?.store_url || shopDomain}
                              readOnly
                              autoComplete="off"
                            />
                            <InlineStack gap="200" blockAlign="center">
                              <Text as="span" variant="bodyMd">Plan Status</Text>
                              <Badge tone={planTone as any}>{storeData?.plan_status || '–'}</Badge>
                            </InlineStack>
                          </FormLayout>
                        )}
                      </BlockStack>
                    )}

                  </Box>
                </Tabs>
              </Card>
            </Layout.Section>

          </Layout>
        </Box>
        {toastMessage && <Toast content={toastMessage} onDismiss={() => setToastMessage('')} />}
      </Page>
    </Frame>
  )
}